import async from 'async';

import { LocationMultiPaymentSupport, LocationState, OrderChannel } from '@biteinc/enums';

import { CryptHelper } from '../../helpers/crypt_helper';

app.LocationDetailsView = app.BaseDetailsView.extend({
  initialize() {
    app.BaseDetailsView.prototype.initialize.apply(this, arguments);

    this.locationWasNotChurned = this.model.get('state') !== LocationState.Churned;
    this.vitrineWasDisabled = !this.model.get('useVitrine');
  },

  getSchema() {
    let schema = app.BaseDetailsView.prototype.getSchema.apply(this);

    const location = this.model;
    if (location.isLive()) {
      schema = app.JsonHelper.deepClone(schema);
    }

    if (!location.supportsPayingWithCash() && schema.fields.allowPayAtCashier) {
      schema.fields.allowPayAtCashier.isPermanent = true;
      schema.fields.allowPayAtCashier.tooltip = {
        text: 'Cash payments are not currently supported by the POS integration.',
      };
    }
    if (!location.supportsTips() && schema.fields.tipsLevel) {
      schema.fields.tipsLevel.isPermanent = true;
      schema.fields.tipsLevel.tooltip = {
        text: location.hasMultiPaymentSupport()
          ? 'Tips are not currently supported by the due to multi-payment support.'
          : 'Tips are not currently supported by the POS integration.',
      };
    }

    return schema;
  },

  _getTabId() {
    return Object.values(this.getSchema().fields)[0].tabId;
  },

  _renderCustomTemplates() {
    if (!app.sessionUser.isBite()) {
      return;
    }

    const tabId = this._getTabId();
    const templatesToPrepend = [];
    if (['sms-settings'].includes(tabId)) {
      templatesToPrepend.push(
        this.valueDisplayTemplate({
          label: 'Notes on Templates:',
          value: `
          The templates below can use the following tokens:
          <br />BRAND_NAME, GUEST_EMAIL, GUEST_NAME, GUEST_PHONE_NUMBER, LOCATION_NAME, LOCATION_PHONE_NUMBER, LOCATION_ADDRESS, ORG_NAME, ORDER_NUMBER, ORDER_PICKUP_TIME, ORDER_TOTAL, ORDER_URL, ORDERED_AT, OUTPOST_NAME, VENDOR_PICKUP_INSTRUCTIONS.
          <br />Consult our client services or eng team for help in setting these up.`,
        }),
      );
    }
    if (templatesToPrepend.length) {
      const $displayGroup = $('<div class="field-group"></div>');
      templatesToPrepend.forEach((template) => {
        $displayGroup.append(template);
      });
      this.$el.prepend($displayGroup);
    }

    // append fields not included in location schema
    const templatesToAppend = [];
    if (tabId === 'bite-settings') {
      templatesToAppend.push(
        this.valueDisplayTemplate({
          label: 'Location ID:',
          value: this.model.id,
        }),
      );
      templatesToAppend.push(
        this.valueDisplayTemplate({
          label: 'Site ID:',
          value: this.model.get('siteId'),
        }),
      );
      templatesToAppend.push(
        this.valueDisplayTemplate({
          label: 'Org ID:',
          value: this.model.get('orgId'),
        }),
      );
      templatesToAppend.push(
        this.valueDisplayTemplate({
          label: 'Expo Printer Username:',
          value: `bite-${this.model.get('shortCode')}`,
        }),
      );
      templatesToAppend.push(
        this.valueDisplayTemplate({
          label: 'Expo Printer Password:',
          // The epson password field has a maximum of 30 characters
          value: CryptHelper.generateHash(this.model.id).substr(0, 30),
        }),
      );
    }
    if (tabId === 'flash-settings' && this.model.get('flashEnabled')) {
      if (this.model.get('multiPaymentSupport') === LocationMultiPaymentSupport.Enabled) {
        app.vendors.forEach((vendor) => {
          templatesToAppend.push(
            this.valueDisplayTemplate({
              label: `Stripe Email for ${vendor.displayName()}:`,
              value: this.model.getStripeEmail(vendor.id),
            }),
          );
        });
      } else {
        templatesToAppend.push(
          this.valueDisplayTemplate({
            label: 'Stripe Email:',
            value: this.model.getStripeEmail(),
          }),
        );
      }
    }
    if (templatesToAppend.length) {
      const $displayGroup = $('<div class="field-group"></div>');
      templatesToAppend.forEach((template) => {
        $displayGroup.append(template);
      });
      this.$el.append($displayGroup);
    }
  },

  // set the payment terminal text field based on payment i9n
  _updateKioskPaymentFields() {
    if (this._getTabId() !== 'kiosk-settings') {
      return;
    }

    const fieldViews = this.fieldGroupView.fieldViewsByField;
    const kioskPaymentI9nSchema = this.model.getKioskPaymentI9nSchema();
    const showIdleText = !!kioskPaymentI9nSchema?.canDisplayText;
    fieldViews.paymentTerminalIdleText.$el.toggle(showIdleText);
  },

  __fieldGroupDidChangeValue() {
    app.BaseDetailsView.prototype.__fieldGroupDidChangeValue.apply(this, arguments);

    this._updateKioskPaymentFields();
  },

  _didSaveModel() {
    this._maybePromptToApplyLinebusterPresets();

    const locationBecameChurned =
      this.locationWasNotChurned && this.model.get('state') === LocationState.Churned;
    if (!locationBecameChurned) {
      return;
    }
    if (!app.sessionUser.isBite()) {
      return;
    }
    const remainingLiveLocations = app.locationList.filter(
      (location) => location.get('state') === LocationState.Live && location.id !== this.model.id,
    );
    const nonLiveLocations = app.locationList.filter(
      (location) =>
        location.get('state') !== LocationState.Live &&
        location.get('state') !== LocationState.BiteOnly &&
        location.id !== this.model.id,
    );
    if (!remainingLiveLocations.length && nonLiveLocations.length) {
      const shouldProceed = confirm(
        'There are no live locations with this account. Did this account churn completely and do you want to mark all other locations as churned as well?',
      );
      if (shouldProceed) {
        const errorMessages = [];
        async.each(
          nonLiveLocations,
          (location, cb) => {
            location.save(
              { state: LocationState.Churned },
              {
                success: () => cb(),
                error: (err) => {
                  errorMessages.push(`Error setting ${location.displayName()} to churned: ${err}`);
                  cb();
                },
              },
            );
          },
          () => {
            if (errorMessages.length) {
              new app.AlertView().show(errorMessages.join('\n'));
            } else {
              app.showSavedToast(
                `Marked ${nonLiveLocations.length} location${
                  nonLiveLocations.length === 1 ? '' : 's'
                } as churned.`,
              );
            }
          },
        );
      }
    }
  },

  // When Vitrine is turned on for a Linebuster channel, offer to apply the standard preset
  // (settings + cover). Detects the disabled->enabled transition at save time.
  _maybePromptToApplyLinebusterPresets() {
    const vitrineJustEnabled = this.vitrineWasDisabled && !!this.model.get('useVitrine');
    // Reset so a later save without a fresh toggle does not re-prompt.
    this.vitrineWasDisabled = !this.model.get('useVitrine');

    if (!vitrineJustEnabled) {
      return;
    }
    if (this.model.get('orderChannel') !== OrderChannel.Linebuster) {
      return;
    }
    if (!confirm('Would you like to apply all the standard Linebuster presets?')) {
      return;
    }

    app.postRequest(`${this.model.url()}/apply-linebuster-presets`, {}, () => {
      app.showSavedToast('Applied the standard Linebuster presets.');
    });
  },

  render() {
    app.BaseDetailsView.prototype.render.apply(this);

    this._updateKioskPaymentFields();

    this._renderCustomTemplates();

    return this;
  },
});
